import { Block } from '@components/Layout/Blocks';
import { Col, ColProps } from '@components/Layout/Col';
import { DisableCover } from '@components/Layout/DisableCover';
import { Row } from '@components/Layout/Row';
import { useMediaQuery } from '@hooks/useMediaQuery';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { SkuType } from '@model/Sku';
import { getSubdomainData } from '@lib/utils';
import { useApi } from '@model/utils/useApi';
import { call } from '@packages/functions';
import { get, upperFirst } from 'lodash';
import * as qs from 'query-string';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SkuCard } from './SkuCard';

interface SkuSelectorProps {
    value: number[];
    categoryId: number;
    subCategoryId?: number;
    formik?: boolean;
    plain?: boolean;
    multiple?: boolean;
    disableCover?: boolean;
    name?: string;
    onLoading?: (state: boolean, cached: boolean) => void;
    onData?: (data: SkuType[], err: any) => void;
    onSelect?: (item: SkuType, id: number, items: SkuType[]) => void;
    component?: any;
    children?: any;
    colProps?: ColProps;
    showCustomizableWheel?: boolean;
    packageId?: number;
    isPackage?: boolean;
}

/**
 * @return {any}
 */
export function SkuSelector(props: SkuSelectorProps) {
    const {
        value: ids,
        formik,
        name,
        plain,
        multiple,
        onLoading,
        onSelect,
        categoryId,
        subCategoryId,
        onData,
        children,
        packageId,
        isPackage,
        showCustomizableWheel,
        component: Component,
    } = props;

    const isMultiple = multiple || typeof multiple === 'undefined';

    const [ids$, setIds$] = useState<number[]>(ids || []);
    const isMobile = useMediaQuery('mobile');
    const isSmallMobile = useMediaQuery('(max-width: 420px)');
    const wholesalerBySubdomain = getSubdomainData();
    const wholesalerParam = { wholesaler_id: wholesalerBySubdomain?.id };

    const colProps = {
        xs: isSmallMobile ? 12 : 6,
        sm: 6,
        md: 4,
        lg: 3,
        padding: isMobile ? 6 : 14,
        // $FlowFixMe
        ...(props.colProps || {}),
    };
    // Update internal selected ids if the prop it's changed
    useEffect(() => {
        if (ids) {
            setIds$(ids);
        }
    }, [ids]);

    const skus$ = useApi<SkuType>({
        url: !isPackage ? 'apparel-skus' : `apparel-skus/${packageId}/package-subcategory-skus`,
        onMount: {
            onLoading,
            method: 'all',
            onError(err) {
                call(onData, [], err);
            },
            after(data) {
                call(onData, [...(data.items || data)]);
            },
            params: {
                category_id: categoryId,
                filter_sizing_kit: true,
                published: true,
                all: true,

                ...(subCategoryId
                    ? {
                          subcategory_id: subCategoryId,
                      }
                    : {}),
                ...wholesalerParam,
                fields: [
                    'id',
                    'name',
                    'category_id',
                    'internal_id',
                    'minimum_order_quantity',
                    ['preview_photo', 'id, thumbnail_path'],
                    ['subcategories', ['id', 'category_id']],
                ],
                order_by: 'name',
                order_direction: 'asc',
            },
            config: {
                paramsSerializer: qs.stringify,
            },
        },
    });

    const { items } = (skus$.data as any) || { items: [] };

    const handleSelect = (item) => () => {
        const { id } = item;
        const newIds = !isMultiple ? id : ids$.includes(id) ? ids$.filter((item) => item !== id) : [...ids$, id];

        setIds$(newIds);

        call(
            onSelect,
            formik
                ? {
                      target: {
                          name: name || 'selectable_skus',
                          value: newIds,
                      },
                  }
                : newIds,
            id,
            items,
        );
    };

    if (!items || items.length === 0) {
        return null;
    }

    const columns = { General: [] };

    items.forEach((item: { [name: string]: any }) => {
        const subcategory = get(item, 'subcategory.name') || 'General';

        columns[subcategory] = [
            ...(columns[subcategory] || []),
            <Component key={item.id} {...colProps}>
                <SkuCard
                    item={item as any}
                    onSelect={handleSelect(item)}
                    isActive={!isMultiple ? ids$ === item.id : ids$.includes(item.id)}
                    showCustomizableWheel={showCustomizableWheel}
                />
            </Component>,
        ];
    });

    const children$ = (
        <>
            {!plain ? (
                <>
                    {Object.keys(columns).map((subCat: string) =>
                        columns[subCat] && columns[subCat].length > 0 ? (
                            <SubCategory key={`col_${subCat}`}>
                                <Divider />

                                <Block w={'100%'} p={isMobile ? '14px 0 6px' : '24px 0 14px'}>
                                    {subCat !== '' ? (
                                        <Row
                                            className={'sub-category__content'}
                                            padding={isMobile ? '0 14px' : '0 28px'}
                                        >
                                            <Typography className={'sub-category__title'} variant={'body1'}>
                                                {upperFirst(subCat)}
                                            </Typography>
                                        </Row>
                                    ) : null}
                                    <Row className={'sub-category__content'} padding={isMobile ? '0 8px' : '0 14px'}>
                                        {columns[subCat]}
                                    </Row>
                                    <DisableCover show={props.disableCover} />
                                </Block>
                            </SubCategory>
                        ) : null,
                    )}
                </>
            ) : (
                Object.keys(columns).reduce((acc: any[], subCat: string) => [...acc, ...columns[subCat]], [])
            )}
            {!plain ? null : <DisableCover show={props.disableCover} />}
        </>
    );

    return typeof children === 'function' ? children(children$) : children$;
}

const Root = styled(Col)`
    & {
        position: relative;
    }
`;

SkuSelector.defaultProps = {
    component: Root,
};

const SubCategory = styled(Row).attrs(() => ({ layout: 'column' }))`
    & {
        .sub-category {
            &__title {
                font-size: 14px;
                line-height: 16px;
                letter-spacing: 0.13px;
                padding: 0;
                @media screen and (max-width: 599px) {
                    margin-bottom: 3px;
                }
            }
            &__content {
                position: relative;
                width: auto;
                min-width: 100%;
            }
        }
    }
`;
